/**
 * Label formatter utilities for resolving human-readable labels
 * from raw internal values (e.g., `ahoy_qualification` → "Qualification").
 *
 * These are pure functions that take schema data as parameters for testability.
 *
 * Note: Stage labels are now looked up via useStages hook, not through Pipeline schemas.
 */

import type { CrmObjectTypeSchema } from '../core/SchemaClient';
import type { PropertyTypeSchema } from '../hooks/usePropertySchema';

/**
 * Convert a label to sentence case: capitalize first word, lowercase
 * subsequent words unless they are fully uppercase (acronyms like ID, URL, CRM).
 *
 * @example
 * toSentenceCase('Phone Number') // 'Phone number'
 * toSentenceCase('Display Name') // 'Display name'
 * toSentenceCase('CRM ID')       // 'CRM ID'
 * toSentenceCase('Email')        // 'Email'
 */
export function toSentenceCase(label: string): string {
  if (!label) {
    return label;
  }
  const words = label.split(' ');
  return words
    .map((word, i) => {
      if (i === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Preserve fully-uppercase words (acronyms): "ID", "URL", "CRM"
      if (word.length > 1 && word === word.toUpperCase()) {
        return word;
      }
      return word.toLowerCase();
    })
    .join(' ');
}

/**
 * Format a value as a fallback when no label is found.
 * Strips `ahoy_` prefix and converts snake_case to sentence case.
 *
 * @example
 * formatFallback('ahoy_qualification') // 'Qualification'
 * formatFallback('ahoy_unknown_entity') // 'Unknown entity'
 * formatFallback('my_custom_stage') // 'My custom stage'
 */
export function formatFallback(value: string): string {
  if (!value) {
    return '';
  }

  // Strip ahoy_ prefix
  let formatted = value.replace(/^ahoy_/, '');

  // Convert snake_case to sentence case
  formatted = formatted
    .split('_')
    .map((word, i) =>
      i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase()
    )
    .join(' ');

  return formatted;
}

/**
 * Find the label for a property option value.
 *
 * @param propertyName - The property_type_name to look up (e.g., 'ahoy_status')
 * @param value - The option value to look up (e.g., 'active')
 * @param propertyTypes - Array of property type schemas to search
 * @returns The option label if found, null otherwise
 *
 * @example
 * getOptionLabel('ahoy_status', 'active', propertyTypes) // 'Active Customer'
 */
export function getOptionLabel(
  propertyName: string,
  value: string,
  propertyTypes: PropertyTypeSchema[]
): string | null {
  if (!propertyName || !value || !propertyTypes.length) {
    return null;
  }

  const property = propertyTypes.find(
    p => p.property_type_name === propertyName
  );
  if (!property?.options) {
    return null;
  }

  const option = property.options.find(o => o.value === value);
  return option?.label ?? null;
}

/**
 * Find the label for a CRM entity type.
 *
 * @param typeName - The entity_type_name to look up (e.g., 'ahoy_deal')
 * @param entityTypes - Array of entity type schemas to search
 * @returns The entity type label if found, null otherwise
 *
 * @example
 * getEntityTypeLabel('ahoy_deal', entityTypes) // 'Deal'
 */
export function getEntityTypeLabel(
  typeName: string,
  entityTypes: CrmObjectTypeSchema[]
): string | null {
  if (!typeName || !entityTypes.length) {
    return null;
  }

  const entityType = entityTypes.find(o => o.entity_type_name === typeName);
  return entityType?.label ?? null;
}
